import { apiAgentes } from '@modules/aawz-intelligence-v2/api/apiAgentes'
import {
  ID_AUTOMATICO,
  ITEM_AUTOMATICO,
  itensDeSelecaoDaResposta,
} from '@modules/aawz-intelligence-v2/utils/agentes-selecionaveis'

// ★ [#172] O seletor de ESPECIALISTA do Modo Agente (ponte decidida pelo
// owner em 16/09/2026; backend no PR #1787).
//
// O defeito: o owner pediu uma simulação de fee fixo, o run nasceu no agente
// de CRM e o modelo respondeu que não tinha a ferramenta —
// `criar_simulacao_fee_fixo` vive no catálogo do agente `fee-fixo`. A tela já
// tinha um seletor com "Especialista em Fee Fixo", desligado
// (`:show-agent-select="false"`), e o corpo do aceite nunca carregou a
// escolha. Não faltava seletor: faltava o fio.
//
// **Dois eixos, um por modo** (decisão do team-lead, opção (a)): aqui mora o
// eixo de ESPECIALISTA (slug do catálogo, 17 roteáveis), e ele só vale no
// Modo Agente. O eixo de PERSONA (`constants/Agentes.js`, 4 itens, usado
// também pela lateral e pelo palette) continua exatamente como está no Modo
// Chat — por isso o estado daqui é PRÓPRIO (`especialistaEscolhidoSlug`) e
// nunca escreve em `agenteAtivoId`, que é do outro eixo. A unificação espera
// o generalista da #171.
//
// A escolha é por RUN: o front manda `agent_slug` em CADA turno enquanto o
// seletor estiver naquele valor — o que está no seletor é o que roda. Fixar
// por conversa é implementável no backend (`nos_agente_fixado`), mas é
// decisão de produto ainda não tomada.
export const viewChatEspecialistaMixin = {
  data() {
    return {
      // `[{id, slug, label, descricao, perguntaGuia}]`, do `GET /agentes`.
      // Nasce vazio e assim permanece se a rota não existir no ambiente: sem
      // seletor, o run nasce no agente default — o comportamento de hoje.
      especialistas: [],
      // O slug escolhido. Vazio = nenhuma escolha = corpo do aceite sem
      // `agent_slug`, byte a byte como antes desta ponte.
      especialistaEscolhidoSlug: '',
    }
  },
  computed: {
    // Sem lista não há o que oferecer. Vale para os três casos que chegam
    // aqui iguais: rota ainda não publicada no ambiente (404 → `{}`), falha
    // de leitura, e `value: []` (escritório sem RBAC de nenhum roteável —
    // hoje inalcançável, piso medido de 11 agentes, mas o custo de tratar é
    // zero e protege de um deploy quebrado).
    mostrarSeletorDeEspecialista() {
      return this.modoAgenteLigado && this.especialistas.length > 0
    },
    // A FONTE do seletor do composer troca com o modo — o componente
    // (`agent-select.vue`) recebe a lista por prop e não sabe de onde ela
    // vem, então não precisa mudar.
    // "Automático" ABRE a lista e é o ativo enquanto ninguém escolheu. Sem
    // ele, `agent-select.vue::agenteAtivo` cairia no primeiro especialista e
    // o chip anunciaria um agente que o aceite não vai usar.
    itensDoSeletorDeEspecialista() {
      return [ITEM_AUTOMATICO].concat(this.especialistas)
    },
    agentesDoComposer() {
      if (!this.mostrarSeletorDeEspecialista) return this.agentesVisiveisDoEscritorio
      return this.itensDoSeletorDeEspecialista
    },
    agenteAtivoIdDoComposer() {
      if (!this.mostrarSeletorDeEspecialista) return this.agenteAtivoId
      return this.especialistaEscolhidoSlug || ID_AUTOMATICO
    },
    especialistaEscolhido() {
      const slug = this.especialistaEscolhidoSlug
      if (!slug) return null
      return this.especialistas.find((e) => e.id === slug) || null
    },
    // A `pergunta_guia` é uma pergunta PRONTA na voz do consultor ("Como
    // migrar este cliente para o fee fixo e o que fazer agora?"), não um
    // placeholder: placeholder some no primeiro caractere digitado e a
    // pergunta nunca chega a ser usada. Por isso ela só aparece quando há
    // espaço para ela — conversa ainda sem mensagem.
    perguntaGuiaDoEspecialista() {
      const escolhido = this.especialistaEscolhido
      if (!escolhido || !escolhido.perguntaGuia) return ''
      // `hasMessages` é o computed que a view já usa para saber se a
      // conversa tem histórico — não invento um segundo.
      return this.hasMessages ? '' : escolhido.perguntaGuia
    },
  },
  mounted() {
    this.carregarEspecialistas()
  },
  methods: {
    /**
     * Busca a lista uma vez. Erro NUNCA vira aviso na cara do consultor: a
     * rota só existe a partir do #1787 e, enquanto não entra em
     * `desenvolvimento`, responde 404 em dev. Um composer que quebrasse por
     * causa disso custaria mais que o seletor vale.
     */
    async carregarEspecialistas() {
      if (!this.modoAgenteLigado) return
      const r = await apiAgentes.listarSelecionaveis()
      this.especialistas = itensDeSelecaoDaResposta(r)
    },
    /**
     * `@change-agent` do composer. Roteia pelo MODO: no Modo Agente a escolha
     * é de especialista; fora dele, segue para o handler de persona que já
     * existia, sem passar perto do estado daqui.
     */
    onTrocarAgenteDoComposer(id) {
      if (this.mostrarSeletorDeEspecialista) {
        this.escolherEspecialista(id)
        return
      }
      this.onTrocarAgente(id)
    },
    /**
     * Guarda a escolha. Só aceita slug que ESTÁ na lista que o backend
     * mandou: um id de outro eixo (persona) chegando aqui viraria uma recusa
     * `agente_desconhecido` no envio seguinte, e o consultor levaria a culpa
     * por um erro de roteamento da tela.
     */
    escolherEspecialista(slug) {
      if (!slug) return
      // Voltar para "Automático" é uma escolha legítima: desfaz a seleção e o
      // turno seguinte nasce no agente default, como antes.
      if (slug === ID_AUTOMATICO) {
        this.especialistaEscolhidoSlug = ''
        return
      }
      const conhecido = this.especialistas.some((e) => e.id === slug)
      if (!conhecido) return
      this.especialistaEscolhidoSlug = slug
    },
  },
}
